// Role-claim + class-pick panel — DOM-panel module, same pattern as
// proto-powergrid's hud.ts: owns its own element refs, exposes init (wire
// callbacks) + update (render from the latest server message).

import { HERO_CLASSES, type HeroClass, isHeroRole, type Role, ROLES, type ServerMessage } from "../shared/types";

type LobbyMessage = Extract<ServerMessage, { type: "lobby" }>;

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing element #${id}`);
  return found as T;
}

const ROLE_LABELS: Record<Role, string> = {
  architect: "Architect",
  hero1: "Hero 1",
  hero2: "Hero 2",
  hero3: "Hero 3",
};

/** Shown in the role-info side panel (see #lobby-role-info) so a new player has some idea what they're signing up for before claiming it. Same text for all 3 hero slots — the role is identical regardless of slot number. */
const ARCHITECT_TOOLTIP =
  "Build the dungeon piece by piece. Goal: kill the heroes without running out of time yourself. " +
  "Grab checkpoints (the rings) to gain more time. Earn gold from minion spawns, completed lines, and target shapes. " +
  "Completing a line also seeds it with a few random traps.";
const HERO_TOOLTIP =
  "Fight your way through the dungeon as it's being built. Goal: survive as long as possible. " +
  "Kill minions to gain xp and level up, becoming harder to kill.";
const ROLE_TOOLTIPS: Record<Role, string> = {
  architect: ARCHITECT_TOOLTIP,
  hero1: HERO_TOOLTIP,
  hero2: HERO_TOOLTIP,
  hero3: HERO_TOOLTIP,
};
const ROLE_INFO_TITLES: Record<Role, string> = {
  architect: "Architect",
  hero1: "Hero",
  hero2: "Hero",
  hero3: "Hero",
};

const CLASS_LABELS: Record<HeroClass, string> = {
  warrior: "Warrior",
  hunter: "Hunter",
  mage: "Mage",
};

const lobbyWrapEl = el<HTMLDivElement>("lobby-wrap");
const rolesEl = el<HTMLDivElement>("lobby-roles");
const classesEl = el<HTMLDivElement>("lobby-classes");
const statusEl = el<HTMLDivElement>("lobby-status");
const startGameBtn = el<HTMLButtonElement>("btn-start-game");
const roleInfoEl = el<HTMLDivElement>("lobby-role-info");
const roleInfoTitleEl = el<HTMLHeadingElement>("lobby-role-info-title");
const roleInfoBodyEl = el<HTMLParagraphElement>("lobby-role-info-body");

/**
 * Drives #lobby-role-info: only shown once you've actually claimed a role —
 * not on hover, which would otherwise make the menu jump around
 * horizontally every time the cursor crosses a role button.
 */
function refreshRoleInfo(myRole: Role | null): void {
  roleInfoEl.classList.toggle("hidden", !myRole);
  if (!myRole) return;
  roleInfoTitleEl.textContent = ROLE_INFO_TITLES[myRole];
  roleInfoBodyEl.textContent = ROLE_TOOLTIPS[myRole];
}

export function initLobby(onClaimRole: (role: Role) => void, onSelectClass: (heroClass: HeroClass) => void, onStartGame: () => void): void {
  startGameBtn.addEventListener("click", onStartGame);

  rolesEl.innerHTML = "";
  for (const role of ROLES) {
    const btn = document.createElement("button");
    btn.className = "lobby-role-btn";
    btn.dataset.role = role;
    btn.textContent = ROLE_LABELS[role];
    btn.addEventListener("click", () => onClaimRole(role));
    rolesEl.appendChild(btn);
  }

  classesEl.innerHTML = "";
  for (const heroClass of HERO_CLASSES) {
    const btn = document.createElement("button");
    btn.className = "lobby-class-btn";
    btn.dataset.class = heroClass;
    btn.textContent = CLASS_LABELS[heroClass];
    btn.addEventListener("click", () => onSelectClass(heroClass));
    classesEl.appendChild(btn);
  }
}

export function updateLobby(message: LobbyMessage): void {
  lobbyWrapEl.classList.toggle("hidden", message.phase !== "lobby");
  if (message.phase !== "lobby") return;

  for (const btn of rolesEl.querySelectorAll<HTMLButtonElement>(".lobby-role-btn")) {
    const role = btn.dataset.role as Role;
    const claimed = !!message.claimedRoles[role];
    const isMine = message.youAreRole === role;
    const heroClass = message.heroClasses[role];
    btn.classList.toggle("claimed", claimed);
    btn.classList.toggle("mine", isMine);
    btn.disabled = claimed && !isMine;
    const classTag = heroClass ? ` [${CLASS_LABELS[heroClass]}]` : "";
    btn.textContent = `${ROLE_LABELS[role]}${claimed ? (isMine ? " (you)" : " — taken") : ""}${classTag}`;
  }

  // Class picking only applies to a hero role you've claimed — the
  // architect has no class, and picking someone else's class makes no sense.
  const myRole = message.youAreRole;
  const showClasses = !!myRole && isHeroRole(myRole);
  classesEl.classList.toggle("hidden", !showClasses);
  if (showClasses) {
    const myClass = myRole ? message.heroClasses[myRole] : undefined;
    for (const btn of classesEl.querySelectorAll<HTMLButtonElement>(".lobby-class-btn")) {
      btn.classList.toggle("mine", btn.dataset.class === myClass);
    }
  }

  // Mirrors server/room.ts's isRosterValid — purely for showing/enabling the
  // button locally; the server is still the one that actually validates it.
  const heroRoles = ROLES.filter((r) => isHeroRole(r) && message.claimedRoles[r]);
  const rosterValid = !!message.claimedRoles.architect && heroRoles.length > 0 && heroRoles.every((r) => message.heroClasses[r]);
  startGameBtn.classList.remove("hidden");
  startGameBtn.disabled = !rosterValid;

  if (!myRole) {
    statusEl.textContent = "Pick a role to join.";
  } else if (isHeroRole(myRole) && !message.heroClasses[myRole]) {
    statusEl.textContent = "Pick a class.";
  } else if (!rosterValid) {
    statusEl.textContent = "Waiting for an Architect and at least one Hero (with a class picked) to join…";
  } else {
    statusEl.textContent = "Ready — press Start Game.";
  }

  refreshRoleInfo(myRole);
}
